import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { apiClient } from '@/utils/api'
import { getTodayDateString, getTomorrowDateString, extractDateInTimezoneSync, getConfiguredTimezone } from '@/utils/timezoneUtils'
import { format, parseISO, subDays, addDays, startOfMonth, startOfWeek } from 'date-fns'
import toast from 'react-hot-toast'
import {
  Clock, CheckCircle, AlertTriangle, Sparkles, History,
  Star, Zap, ChevronRight, Eye, TrendingUp, Calendar, Trophy, ClipboardList
} from 'lucide-react'
import { EmptyState } from '@/components/EmptyState'
import { ChoreStatusBadge } from '@/components/ChoreStatusStepper'
import { useConfetti } from '@/components/ConfettiCelebration'
import { getIconDisplay } from '@/utils/iconMapping'
import { sortedChoreSteps } from '@/utils/choreSteps'

interface Assignment {
  id: string
  chore_id: string
  assigned_to?: string | null
  user_id?: string
  status: string
  due_date: string
  points_earned?: number
  points_offered?: number
  is_bonus?: boolean
  bonus_multiplier?: number
  completed_at?: string
  verified_at?: string
  chore: {
    name: string
    description?: string
    base_points: number
    estimated_minutes: number
    icon?: string
    category?: string
  }
}

interface PointsStats {
  available: number
  thisWeek: number
  thisMonth: number
  lifetime: number
}

export const KidChoresDesktop: React.FC = () => {
  const { user } = useAuth()
  const [todayTasks, setTodayTasks] = useState<Assignment[]>([])
  const [upcomingTasks, setUpcomingTasks] = useState<Assignment[]>([])
  const [overdueTasks, setOverdueTasks] = useState<Assignment[]>([])
  const [bonusTasks, setBonusTasks] = useState<Assignment[]>([])
  const [historyTasks, setHistoryTasks] = useState<Assignment[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'today' | 'upcoming' | 'bonus' | 'history'>('today')
  const [completingTask, setCompletingTask] = useState<string | null>(null)
  const [pointsStats, setPointsStats] = useState<PointsStats>({
    available: 0,
    thisWeek: 0,
    thisMonth: 0,
    lifetime: 0
  })
  const [verificationEnabled, setVerificationEnabled] = useState<boolean>(true)

  // Confetti celebration
  const { showConfetti, celebrate, onConfettiComplete, ConfettiComponent } = useConfetti()

  useEffect(() => {
    if (user) {
      loadTasks()
      loadPointsStats()
      fetchFeatureToggles()
    }
  }, [user])

  const fetchFeatureToggles = async () => {
    try {
      const data = await apiClient.get('/api/family/settings')
      const toggles = data.feature_toggles || data.settings?.feature_toggles || {}
      setVerificationEnabled(toggles.chore_verification !== false)
    } catch (error) {
      console.error('Error fetching feature toggles:', error)
      setVerificationEnabled(true)
    }
  }

  const loadPointsStats = async () => {
    if (!user) return

    try {
      // Calculate week and month dates
      const now = new Date()
      const weekStart = format(startOfWeek(now, { weekStartsOn: 0 }), 'yyyy-MM-dd') // Sunday
      const monthStart = format(startOfMonth(now), 'yyyy-MM-dd')
      const today = format(now, 'yyyy-MM-dd')

      // Fetch completed AND verified assignments for this week and month
      const [weekCompletedRes, weekVerifiedRes, monthCompletedRes, monthVerifiedRes] = await Promise.all([
        apiClient.get(`/api/assignments?status=completed&user_id=${user.id}&start_date=${weekStart}&end_date=${today}`).catch(() => []),
        apiClient.get(`/api/assignments?status=verified&user_id=${user.id}&start_date=${weekStart}&end_date=${today}`).catch(() => []),
        apiClient.get(`/api/assignments?status=completed&user_id=${user.id}&start_date=${monthStart}&end_date=${today}`).catch(() => []),
        apiClient.get(`/api/assignments?status=verified&user_id=${user.id}&start_date=${monthStart}&end_date=${today}`).catch(() => [])
      ])

      // Extract arrays from responses
      const extractArray = (res: any) => Array.isArray(res) ? res : (res?.assignments || res?.data || [])

      const weekCompleted = extractArray(weekCompletedRes)
      const weekVerified = extractArray(weekVerifiedRes)
      const monthCompleted = extractArray(monthCompletedRes)
      const monthVerified = extractArray(monthVerifiedRes)

      // Combine and deduplicate by ID
      const weeklyAssignments = [...weekCompleted, ...weekVerified]
        .filter((a, i, arr) => arr.findIndex(x => x.id === a.id) === i)

      const monthlyAssignments = [...monthCompleted, ...monthVerified]
        .filter((a, i, arr) => arr.findIndex(x => x.id === a.id) === i)

      // Calculate totals from assignments
      const weeklyPoints = weeklyAssignments
        .reduce((sum: number, a: Assignment) => sum + (a.points_earned || a.chore?.base_points || 0), 0)

      const monthlyPoints = monthlyAssignments
        .reduce((sum: number, a: Assignment) => sum + (a.points_earned || a.chore?.base_points || 0), 0)

      setPointsStats({
        available: (user as any).available_points ?? user.total_points ?? 0,
        thisWeek: weeklyPoints,
        thisMonth: monthlyPoints,
        lifetime: (user as any).lifetime_points_earned || user.total_points || 0
      })
    } catch (error) {
      console.error('Error loading points stats:', error)
      // Fallback to user data
      setPointsStats({
        available: (user as any).available_points ?? user.total_points ?? 0,
        thisWeek: 0,
        thisMonth: 0,
        lifetime: (user as any).lifetime_points_earned || user.total_points || 0
      })
    }
  }

  const loadTasks = async () => {
    if (!user) return
    setLoading(true)

    try {
      // Initialize timezone cache before sync operations
      await getConfiguredTimezone()

      const todayStr = await getTodayDateString()
      const now = new Date()
      const weekEndStr = format(addDays(now, 7), 'yyyy-MM-dd') // Next 7 days

      // Fetch active assignments
      const response = await apiClient.get('/api/assignments')
      const assignments = response?.assignments || response?.data || []

      // Filter to this kid's assignments (check both fields)
      const myAssignments = assignments.filter((a: Assignment) =>
        a.assigned_to === user.id || a.user_id === user.id
      )

      // Categorize by date and status
      const today: Assignment[] = []
      const upcoming: Assignment[] = []
      const overdue: Assignment[] = []
      const bonus: Assignment[] = []

      myAssignments.forEach((assignment: Assignment) => {
        // Use timezone-aware date extraction to avoid UTC/local date mismatch
        const dueDate = assignment.due_date ? extractDateInTimezoneSync(assignment.due_date) : null
        const isCompleted = assignment.status === 'completed' || assignment.status === 'verified'

        if (isCompleted) {
          // Skip completed in main list, we'll fetch history separately
          return
        } else if (assignment.is_bonus) {
          bonus.push(assignment)
        } else if (dueDate && dueDate < todayStr) {
          overdue.push(assignment)
        } else if (dueDate === todayStr) {
          today.push(assignment)
        } else if (dueDate && dueDate > todayStr && dueDate <= weekEndStr) {
          upcoming.push(assignment)
        }
      })

      // Sort upcoming by due date
      upcoming.sort((a, b) => (a.due_date || '').localeCompare(b.due_date || ''))

      setTodayTasks(today)
      setUpcomingTasks(upcoming)
      setOverdueTasks(overdue)
      setBonusTasks(bonus)

      // Fetch history from API with proper query (last 30 days)
      await loadHistory()

    } catch (error) {
      console.error('Error loading tasks:', error)
      toast.error('Failed to load tasks')
    } finally {
      setLoading(false)
    }
  }

  const loadHistory = async () => {
    if (!user) return

    try {
      const now = new Date()
      const thirtyDaysAgo = format(subDays(now, 30), 'yyyy-MM-dd')
      const today = format(now, 'yyyy-MM-dd')

      // Fetch completed and verified assignments
      const [completedRes, verifiedRes] = await Promise.all([
        apiClient.get(`/api/assignments?status=completed&user_id=${user.id}&start_date=${thirtyDaysAgo}&end_date=${today}`).catch(() => []),
        apiClient.get(`/api/assignments?status=verified&user_id=${user.id}&start_date=${thirtyDaysAgo}&end_date=${today}`).catch(() => [])
      ])

      const completed = Array.isArray(completedRes) ? completedRes : (completedRes?.assignments || completedRes?.data || [])
      const verified = Array.isArray(verifiedRes) ? verifiedRes : (verifiedRes?.assignments || verifiedRes?.data || [])

      // Combine and sort by completion date (most recent first)
      const allHistory = [...completed, ...verified]
        .sort((a, b) => {
          const dateA = a.completed_at || a.verified_at || ''
          const dateB = b.completed_at || b.verified_at || ''
          return dateB.localeCompare(dateA)
        })
        .slice(0, 30) // Limit to 30 most recent

      setHistoryTasks(allHistory)
    } catch (error) {
      console.error('Error loading history:', error)
    }
  }

  const handleCompleteTask = async (task: Assignment) => {
    if (completingTask) return
    setCompletingTask(task.id)

    try {
      await apiClient.post(`/api/assignments/${task.id}/complete`)

      const points = task.is_bonus && task.bonus_multiplier
        ? Math.round(task.chore.base_points * task.bonus_multiplier)
        : task.chore.base_points

      toast.success(`Completed! +${points} points`)
      celebrate() // Trigger confetti celebration
      loadTasks()
      loadPointsStats()
    } catch (error) {
      console.error('Error completing task:', error)
      toast.error('Failed to complete task')
    } finally {
      setCompletingTask(null)
    }
  }

  const renderTask = (task: Assignment, showComplete: boolean = true) => {
    const isCompleting = completingTask === task.id
    const points = task.points_earned || (task.is_bonus && task.bonus_multiplier
      ? Math.round(task.chore.base_points * task.bonus_multiplier)
      : task.chore.base_points)

    return (
      <div
        key={task.id}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow"
      >
        <div className="flex items-start gap-4">
          <div className="text-3xl">{getIconDisplay(task.chore.icon)}</div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold text-gray-900">{task.chore.name}</h3>
                {task.chore.description && (
                  <p className="text-sm text-gray-500 mt-1">{task.chore.description}</p>
                )}
                {(() => {
                  const taskSteps = sortedChoreSteps((task.chore as any)?.steps)
                  if (taskSteps.length === 0) return null
                  return (
                    <ol className="mt-2 ml-4 space-y-1 list-decimal text-sm text-gray-600">
                      {taskSteps.map((step, i) => (
                        <li key={i}>{step.text}</li>
                      ))}
                    </ol>
                  )
                })()}
              </div>

              <div className="flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold whitespace-nowrap">
                <Star className="w-4 h-4" />
                {points}
                {task.is_bonus && task.bonus_multiplier && (
                  <span className="text-xs ml-1">({task.bonus_multiplier}x)</span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                ~{task.chore.estimated_minutes} min
              </div>
              {/* Status progression badge */}
              <ChoreStatusBadge
                status={task.verified_at ? 'verified' : task.status}
                verificationEnabled={verificationEnabled}
                isParent={false}
                onAdvance={showComplete ? async () => {
                  await handleCompleteTask(task)
                } : undefined}
                loading={isCompleting}
              />
              {task.completed_at && !task.verified_at && (
                <div className="flex items-center gap-1 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  {format(parseISO(task.completed_at), 'MMM d')}
                </div>
              )}
            </div>
          </div>

          {showComplete && task.status === 'pending' && (
            <button
              onClick={() => handleCompleteTask(task)}
              disabled={isCompleting}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              {isCompleting ? 'Saving...' : 'Done!'}
            </button>
          )}
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading chores...</p>
        </div>
      </div>
    )
  }

  const tabs = [
    { id: 'today', label: 'Today', icon: Clock, count: todayTasks.length + overdueTasks.length },
    { id: 'upcoming', label: 'This Week', icon: Calendar, count: upcomingTasks.length },
    { id: 'bonus', label: 'Bonus', icon: Sparkles, count: bonusTasks.length },
    { id: 'history', label: 'History', icon: History, count: historyTasks.length }
  ]

  return (
    <div className="max-w-5xl mx-auto">
      {/* Points Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-2 mb-2 opacity-90">
            <Star className="w-5 h-5" />
            <span className="text-sm font-medium">Available</span>
          </div>
          <div className="text-3xl font-bold">{pointsStats.available.toLocaleString()}</div>
          <div className="text-xs opacity-75 mt-1">points to spend</div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-2 mb-2 opacity-90">
            <Calendar className="w-5 h-5" />
            <span className="text-sm font-medium">This Week</span>
          </div>
          <div className="text-3xl font-bold">{pointsStats.thisWeek.toLocaleString()}</div>
          <div className="text-xs opacity-75 mt-1">earned this week</div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-2 mb-2 opacity-90">
            <TrendingUp className="w-5 h-5" />
            <span className="text-sm font-medium">This Month</span>
          </div>
          <div className="text-3xl font-bold">{pointsStats.thisMonth.toLocaleString()}</div>
          <div className="text-xs opacity-75 mt-1">earned this month</div>
        </div>

        <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-center gap-2 mb-2 opacity-90">
            <Trophy className="w-5 h-5" />
            <span className="text-sm font-medium">Lifetime</span>
          </div>
          <div className="text-3xl font-bold">{pointsStats.lifetime.toLocaleString()}</div>
          <div className="text-xs opacity-75 mt-1">total earned</div>
        </div>
      </div>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Chores</h1>
        <p className="text-gray-500 mt-1">
          {todayTasks.length + overdueTasks.length} tasks to complete today
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
        <div className="flex border-b border-gray-200">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
                {tab.count > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-4">
        {activeTab === 'today' && (
          <>
            {overdueTasks.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  <h2 className="font-semibold">Overdue</h2>
                </div>
                <div className="space-y-3">
                  {overdueTasks.map(task => renderTask(task))}
                </div>
              </div>
            )}

            {todayTasks.length > 0 ? (
              <div className="space-y-3">
                {todayTasks.map(task => renderTask(task))}
              </div>
            ) : overdueTasks.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl shadow-sm">
                <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900">All done for today!</h3>
                <p className="text-gray-500 mt-2">Check back tomorrow for new tasks</p>
              </div>
            ) : null}
          </>
        )}

        {activeTab === 'upcoming' && (
          upcomingTasks.length > 0 ? (
            <div className="space-y-3">
              {upcomingTasks.map(task => {
                const dueDate = task.due_date ? extractDateInTimezoneSync(task.due_date) : null
                return (
                  <div key={task.id}>
                    <div className="text-xs text-gray-500 font-medium mb-2 px-1">
                      {dueDate && format(parseISO(dueDate), 'EEEE, MMM d')}
                    </div>
                    {renderTask(task, false)}
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl shadow-sm">
              <Calendar className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900">No upcoming tasks</h3>
              <p className="text-gray-500 mt-2">Your week looks clear!</p>
            </div>
          )
        )}

        {activeTab === 'bonus' && (
          bonusTasks.length > 0 ? (
            <div className="space-y-3">
              {bonusTasks.map(task => renderTask(task))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl shadow-sm">
              <Sparkles className="w-16 h-16 mx-auto text-amber-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900">No bonus tasks available</h3>
              <p className="text-gray-500 mt-2">Check back later for bonus opportunities!</p>
            </div>
          )
        )}

        {activeTab === 'history' && (
          historyTasks.length > 0 ? (
            <div className="space-y-3">
              {historyTasks.map(task => renderTask(task, false))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl shadow-sm">
              <History className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold text-gray-900">No completed tasks yet</h3>
              <p className="text-gray-500 mt-2">Start completing tasks to see your history</p>
            </div>
          )
        )}
      </div>

      {/* Confetti celebration */}
      <ConfettiComponent />
    </div>
  )
}
